import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Inbox,
  Link2,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldOff,
  Send,
  Download,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import ViewerActivityFeed from "@/components/ViewerActivityFeed";

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "opened", label: "Opened" },
  { value: "viewed", label: "Viewed" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
  { value: "suppressed", label: "Suppressed" },
  { value: "undeliverable", label: "Undeliverable" },
  { value: "spam_reported", label: "Spam reported" },
];

const STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-700",
  sent: "bg-indigo-100 text-indigo-700",
  opened: "bg-blue-100 text-blue-700",
  viewed: "bg-emerald-100 text-emerald-700",
  expired: "bg-slate-100 text-slate-500",
  revoked: "bg-rose-100 text-rose-700",
  // R3-Phase-5 — every "delivery dead-end" status pills in rose to
  // visually group them with revoked.  The pill tooltip carries the
  // specific reason (undeliverable_reason on the row) so the operator
  // can disambiguate without leaving the table.
  suppressed: "bg-rose-100 text-rose-700",
  undeliverable: "bg-rose-100 text-rose-700",
  spam_reported: "bg-rose-100 text-rose-700",
};

const STATUS_LABEL = {
  pending: "Pending",
  sent: "Sent",
  opened: "Opened",
  viewed: "Viewed",
  expired: "Expired",
  revoked: "Revoked",
  suppressed: "Suppressed",
  undeliverable: "Undeliverable",
  spam_reported: "Spam reported",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${cls}`}
      data-testid={`shares-status-${status}`}
    >
      {status}
    </span>
  );
}

export default function SharesAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusValue, setStatusValue] = useState("all");
  const [operatorEmail, setOperatorEmail] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientDomain, setRecipientDomain] = useState("");
  const [issuedAfter, setIssuedAfter] = useState("");
  const [issuedBefore, setIssuedBefore] = useState("");
  const [runId, setRunId] = useState("all");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null); // "revoke" | "resend" | null

  const filters = useMemo(
    () => ({ statusValue, operatorEmail, recipientSearch, recipientDomain, issuedAfter, issuedBefore, runId }),
    [statusValue, operatorEmail, recipientSearch, recipientDomain, issuedAfter, issuedBefore, runId],
  );

  const runsQuery = useQuery({
    queryKey: ["shares-runs-with-invitations"],
    queryFn: async () => (await api.get("/shares/runs-with-invitations")).data,
    staleTime: 60_000,
  });
  const runOptions = runsQuery.data?.items ?? [];

  const sharesQuery = useQuery({
    queryKey: ["admin-shares", filters, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      if (statusValue !== "all") params.append("status", statusValue);
      if (operatorEmail.trim()) params.set("operator_email", operatorEmail.trim().toLowerCase());
      if (recipientSearch.trim()) params.set("recipient_search", recipientSearch.trim());
      if (recipientDomain.trim()) params.set("recipient_domain", recipientDomain.trim().toLowerCase());
      if (issuedAfter) params.set("issued_after", `${issuedAfter}T00:00:00Z`);
      if (issuedBefore) params.set("issued_before", `${issuedBefore}T23:59:59Z`);
      if (runId && runId !== "all") params.set("run_id", runId);
      return (await api.get(`/shares?${params.toString()}`)).data;
    },
    keepPreviousData: true,
  });

  const items = sharesQuery.data?.items ?? [];
  const total = sharesQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const allOnPageSelected = items.length > 0 && items.every((it) => selected.has(it.invitation_id));

  function toggleAll() {
    if (allOnPageSelected) {
      const next = new Set(selected);
      items.forEach((it) => next.delete(it.invitation_id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      items.forEach((it) => next.add(it.invitation_id));
      setSelected(next);
    }
  }

  function toggleOne(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function clearSelection() {
    setSelected(new Set());
  }

  const bulkRevoke = useMutation({
    mutationFn: async () => {
      return (await api.post("/invitations/bulk-revoke", { invitation_ids: selectedIds })).data;
    },
    onSuccess: (data) => {
      const summary = summarize(data?.items || []);
      toast.success(`Bulk revoke: ${summary}`);
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ["admin-shares"] });
      queryClient.invalidateQueries({ queryKey: ["viewer-activity-feed"] });
      setConfirmAction(null);
    },
    onError: (err) => {
      toast.error(formatApiError(err));
      setConfirmAction(null);
    },
  });

  const bulkResend = useMutation({
    mutationFn: async () => {
      return (await api.post("/invitations/bulk-resend", { invitation_ids: selectedIds })).data;
    },
    onSuccess: (data) => {
      const summary = summarize(data?.items || []);
      toast.success(`Bulk resend: ${summary}`);
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ["admin-shares"] });
      setConfirmAction(null);
    },
    onError: (err) => {
      toast.error(formatApiError(err));
      setConfirmAction(null);
    },
  });

  function resetPageAnd(setter) {
    return (v) => {
      setPage(0);
      setter(v);
    };
  }

  return (
    <div className="space-y-6" data-testid="shares-admin-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900">
            <Link2 size={20} className="text-indigo-600" />
            Admin · Demo Shares
          </h1>
          <p className="max-w-2xl text-sm text-slate-500">
            Every invitation issued across all demos. Filter by status, operator, or recipient,
            then bulk-revoke or bulk-resend. Click a row to drill into the per-invitation timeline.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => queryClient.invalidateQueries({ queryKey: ["admin-shares"] })}
          disabled={sharesQuery.isFetching}
          data-testid="shares-refresh"
        >
          <RefreshCw size={14} className={sharesQuery.isFetching ? "mr-1.5 animate-spin" : "mr-1.5"} />
          Refresh
        </Button>
      </header>

      {/* Viewer activity tile */}
      <ViewerActivityFeed limit={10} dataTestidPrefix="shares-activity" />

      {/* Filters */}
      <section
        className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4"
        data-testid="shares-filters"
      >
        <div className="space-y-1">
          <label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <Filter size={11} /> Status
          </label>
          <Select value={statusValue} onValueChange={resetPageAnd(setStatusValue)}>
            <SelectTrigger className="h-9 text-xs" data-testid="shares-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`shares-filter-status-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Operator email
          </label>
          <Input
            value={operatorEmail}
            onChange={(e) => { setPage(0); setOperatorEmail(e.target.value); }}
            placeholder="alice@eyvo.com"
            className="h-9 text-xs"
            data-testid="shares-filter-operator"
          />
        </div>
        <div className="space-y-1">
          <label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <Search size={11} /> Recipient (contains)
          </label>
          <Input
            value={recipientSearch}
            onChange={(e) => { setPage(0); setRecipientSearch(e.target.value); }}
            placeholder="acme"
            className="h-9 text-xs"
            data-testid="shares-filter-recipient"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Recipient domain
          </label>
          <Input
            value={recipientDomain}
            onChange={(e) => { setPage(0); setRecipientDomain(e.target.value); }}
            placeholder="acme.com"
            className="h-9 text-xs"
            data-testid="shares-filter-domain"
          />
        </div>
      </section>

      {/* More filters disclosure: date range + run selector */}
      <section
        className="rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="shares-more-filters"
      >
        <div className="flex w-full items-stretch border-b border-transparent">
          <button
            type="button"
            onClick={() => setShowMoreFilters((v) => !v)}
            className="flex flex-1 items-center justify-between px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50"
            data-testid="shares-more-filters-toggle"
          >
            <span className="flex items-center gap-1.5">
              <Filter size={11} /> More filters
              {(issuedAfter || issuedBefore || (runId && runId !== "all")) ? (
                <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-1.5 py-0 text-[9px] text-indigo-700">
                  active
                </span>
              ) : null}
            </span>
            <span aria-hidden="true" className="text-slate-400">
              {showMoreFilters ? "▾" : "▸"}
            </span>
          </button>
          <ExportCsvButton
            statusValue={statusValue}
            operatorEmail={operatorEmail}
            recipientSearch={recipientSearch}
            recipientDomain={recipientDomain}
            issuedAfter={issuedAfter}
            issuedBefore={issuedBefore}
            runId={runId}
            total={total}
          />
        </div>
        {showMoreFilters && (
          <div
            className="grid grid-cols-1 gap-3 border-t border-slate-100 px-4 py-3 sm:grid-cols-3"
            data-testid="shares-more-filters-panel"
          >
            <div className="space-y-1">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Issued after
              </label>
              <Input
                type="date"
                value={issuedAfter}
                onChange={(e) => { setPage(0); setIssuedAfter(e.target.value); }}
                className="h-9 text-xs"
                data-testid="shares-filter-issued-after"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Issued before
              </label>
              <Input
                type="date"
                value={issuedBefore}
                onChange={(e) => { setPage(0); setIssuedBefore(e.target.value); }}
                className="h-9 text-xs"
                data-testid="shares-filter-issued-before"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Run
              </label>
              <Select value={runId} onValueChange={(v) => { setPage(0); setRunId(v); }}>
                <SelectTrigger className="h-9 text-xs" data-testid="shares-filter-run">
                  <SelectValue placeholder="Any run" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="shares-filter-run-all">
                    Any run
                  </SelectItem>
                  {runOptions.map((r) => {
                    const mix = r.status_mix || {};
                    const parts = Object.entries(mix)
                      .filter(([, v]) => v > 0)
                      .map(([k, v]) => `${v} ${k}`);
                    const mixLabel = parts.length ? ` (${parts.join(" · ")})` : "";
                    const total = r.invitation_count ?? 0;
                    const runLabel = r.run_number != null
                      ? `#${String(r.run_number).padStart(4, "0")}`
                      : r.run_id.slice(0, 6) + "…";
                    return (
                      <SelectItem
                        key={r.run_id}
                        value={r.run_id}
                        data-testid={`shares-filter-run-${r.run_id}`}
                      >
                        {runLabel}
                        {" · "}
                        {r.workflow_name || r.title || "—"}
                        {" · "}
                        <span className="text-slate-500">{total}{mixLabel}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </section>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-2.5 text-sm text-indigo-900"
          data-testid="shares-bulk-bar"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 size={15} />
            <span>
              <strong>{selectedIds.length}</strong> invitation{selectedIds.length === 1 ? "" : "s"} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmAction("resend")}
              disabled={bulkResend.isPending || bulkRevoke.isPending}
              data-testid="shares-bulk-resend"
            >
              {bulkResend.isPending ? (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              ) : (
                <Send size={13} className="mr-1.5" />
              )}
              Resend selected
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              onClick={() => setConfirmAction("revoke")}
              disabled={bulkResend.isPending || bulkRevoke.isPending}
              data-testid="shares-bulk-revoke"
            >
              {bulkRevoke.isPending ? (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              ) : (
                <ShieldOff size={13} className="mr-1.5" />
              )}
              Revoke selected
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="shares-bulk-clear">
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="w-10 px-3 py-2.5">
                <Checkbox
                  checked={allOnPageSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all on page"
                  data-testid="shares-select-all"
                />
              </th>
              <th className="px-3 py-2.5">Recipient</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Run / Workflow</th>
              <th className="px-3 py-2.5">Operator</th>
              <th className="px-3 py-2.5">Issued</th>
              <th className="px-3 py-2.5">Expires</th>
              <th className="px-3 py-2.5 text-right">Views</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sharesQuery.isLoading ? (
              <tr>
                <td colSpan={8} className="py-6 text-center text-xs text-slate-500">
                  <Loader2 size={14} className="mr-2 inline animate-spin" /> Loading invitations…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-xs text-slate-500" data-testid="shares-empty">
                  <Inbox size={18} className="mx-auto mb-1 text-slate-400" />
                  No invitations match the current filters.
                </td>
              </tr>
            ) : (
              items.map((inv) => (
                <Row
                  key={inv.invitation_id}
                  inv={inv}
                  selected={selected.has(inv.invitation_id)}
                  onToggle={() => toggleOne(inv.invitation_id)}
                  onOpen={() => navigate(`/shares/${inv.invitation_id}`)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span data-testid="shares-page-info">
          Page {page + 1} of {pageCount} · {total} invitation{total === 1 ? "" : "s"}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 0 || sharesQuery.isFetching}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            data-testid="shares-prev"
          >
            <ChevronLeft size={14} className="mr-1" /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= pageCount || sharesQuery.isFetching}
            onClick={() => setPage((p) => p + 1)}
            data-testid="shares-next"
          >
            Next <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>

      {/* Confirmation dialog for bulk revoke + bulk resend */}
      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => { if (!open) setConfirmAction(null); }}
      >
        <AlertDialogContent data-testid="shares-bulk-confirm-dialog">
          {confirmAction === "revoke" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="shares-bulk-confirm-title">
                  Revoke {selectedIds.length} invitation{selectedIds.length === 1 ? "" : "s"}?
                </AlertDialogTitle>
                <AlertDialogDescription data-testid="shares-bulk-confirm-body">
                  The selected viewers will immediately lose access. This action cannot be undone. Continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  data-testid="shares-bulk-confirm-cancel"
                  disabled={bulkRevoke.isPending}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600"
                  onClick={(e) => { e.preventDefault(); bulkRevoke.mutate(); }}
                  disabled={bulkRevoke.isPending}
                  data-testid="shares-bulk-confirm-revoke"
                >
                  {bulkRevoke.isPending ? (
                    <Loader2 size={13} className="mr-1.5 animate-spin" />
                  ) : (
                    <ShieldOff size={13} className="mr-1.5" />
                  )}
                  Revoke {selectedIds.length} invitation{selectedIds.length === 1 ? "" : "s"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : confirmAction === "resend" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="shares-bulk-confirm-title">
                  Resend {selectedIds.length} invitation{selectedIds.length === 1 ? "" : "s"}?
                </AlertDialogTitle>
                <AlertDialogDescription data-testid="shares-bulk-confirm-body">
                  Each invitation is capped at 3 resends per 24 hours; any rows over the cap will be
                  skipped and the rest will still be re-sent. Continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  data-testid="shares-bulk-confirm-cancel"
                  disabled={bulkResend.isPending}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-600"
                  onClick={(e) => { e.preventDefault(); bulkResend.mutate(); }}
                  disabled={bulkResend.isPending}
                  data-testid="shares-bulk-confirm-resend"
                >
                  {bulkResend.isPending ? (
                    <Loader2 size={13} className="mr-1.5 animate-spin" />
                  ) : (
                    <Send size={13} className="mr-1.5" />
                  )}
                  Resend {selectedIds.length} invitation{selectedIds.length === 1 ? "" : "s"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function summarize(items) {
  const counts = items.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  return (
    Object.entries(counts)
      .map(([k, v]) => `${v} ${k}`)
      .join(" · ") || "no items"
  );
}

function ExportCsvButton({
  statusValue,
  operatorEmail,
  recipientSearch,
  recipientDomain,
  issuedAfter,
  issuedBefore,
  runId,
  total,
}) {
  const over = total > 5000;
  const params = new URLSearchParams();
  if (statusValue && statusValue !== "all") params.append("status", statusValue);
  if (operatorEmail?.trim()) params.set("operator_email", operatorEmail.trim().toLowerCase());
  if (recipientSearch?.trim()) params.set("recipient_search", recipientSearch.trim());
  if (recipientDomain?.trim()) params.set("recipient_domain", recipientDomain.trim().toLowerCase());
  if (issuedAfter) params.set("issued_after", `${issuedAfter}T00:00:00Z`);
  if (issuedBefore) params.set("issued_before", `${issuedBefore}T23:59:59Z`);
  if (runId && runId !== "all") params.set("run_id", runId);
  const href = `${BACKEND_URL}/api/shares/export.csv?${params.toString()}`;
  const title = over
    ? "Refine filters to export (max 5,000 rows)"
    : `Download the current ${total} filtered row${total === 1 ? "" : "s"} as CSV`;

  function handleClick(e) {
    if (over) {
      e.preventDefault();
    }
  }

  // R3 UI close-out — discoverability fix.  The button used to be flush
  // against the "More filters" toggle with a thin left border, which
  // made it read as part of the disclosure header instead of as a
  // primary inline action.  It now renders as a proper outlined button
  // with its own visible chrome, indigo-700 text/border, and a margin so
  // it never collides with the toggle row.
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={handleClick}
      title={title}
      aria-disabled={over}
      tabIndex={over ? -1 : 0}
      className={`my-2 mr-3 inline-flex shrink-0 items-center gap-1.5 self-center rounded-md border bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide shadow-sm transition-colors ${
        over
          ? "cursor-not-allowed border-slate-200 text-slate-400"
          : "border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400"
      }`}
      data-testid="shares-export-csv"
      data-over-limit={over ? "true" : "false"}
    >
      <Download size={12} aria-hidden="true" />
      <span>Export CSV</span>
    </a>
  );
}

function Row({ inv, selected, onToggle, onOpen }) {
  return (
    <tr
      className={selected ? "bg-indigo-50/40" : "hover:bg-slate-50"}
      data-testid={`shares-row-${inv.invitation_id}`}
    >
      <td className="px-3 py-2.5 align-top">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          aria-label={`Select ${inv.invited_email}`}
          data-testid={`shares-select-${inv.invitation_id}`}
        />
      </td>
      <td className="px-3 py-2.5 align-top">
        <button
          type="button"
          onClick={onOpen}
          className="block text-left text-sm font-medium text-indigo-700 hover:underline"
          data-testid={`shares-open-${inv.invitation_id}`}
        >
          {inv.invited_email}
        </button>
        {inv.invited_company_name ? (
          <div className="text-[10px] text-slate-400">{inv.invited_company_name}</div>
        ) : null}
      </td>
      <td className="px-3 py-2.5 align-top"><StatusPill status={inv.status} reason={inv.undeliverable_reason} /></td>
      <td className="px-3 py-2.5 align-top text-[12px]">
        <Link to={`/runs/${inv.run_id}`} className="text-indigo-700 hover:underline">
          {inv.run_number != null ? `#${inv.run_number}` : `run ${inv.run_id.slice(0, 6)}…`}
        </Link>
        <div className="text-[10px] text-slate-400">
          {inv.workflow_name || inv.run_label || "—"}
        </div>
      </td>
      <td className="px-3 py-2.5 align-top text-[11px] text-slate-600">
        {inv.invited_by_user_id || "—"}
      </td>
      <td className="px-3 py-2.5 align-top text-[11px] text-slate-600">{fmtDate(inv.issued_at)}</td>
      <td className="px-3 py-2.5 align-top text-[11px] text-slate-600">
        {inv.expires_at ? fmtDate(inv.expires_at) : <span className="text-slate-400">never</span>}
      </td>
      <td className="px-3 py-2.5 align-top text-right text-[11px] text-slate-600">
        <span data-testid={`shares-row-views-${inv.invitation_id}`}>{inv.view_count ?? 0}</span>
        {inv.last_viewed_at ? (
          <div className="text-[10px] text-slate-400">last {fmtDate(inv.last_viewed_at)}</div>
        ) : null}
      </td>
    </tr>
  );
}
